import tracker from './tracker';
import { RootState, store } from '../../store';
import { stateName, EVENT } from './enums';
import { isNil, omitBy } from 'es-toolkit';
import jsBridge from '@utils/jsBridge';
import { DEVICE_MOBILE, DEVICE_DESKTOP } from '@hooks/useDeviceType';
import { getProfileInfoForInstrumentation } from './profileHelpers';
import { isProdEnv } from '@utils/index';

type TrackingState = {
  rootState: RootState;
};

type StateNameType = (typeof stateName)[keyof typeof stateName];
type EventType = (typeof EVENT)[keyof typeof EVENT];

let devEventSeq = 0;

function formatVal(v: unknown): string {
  if (v == null) return '(nil)';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function formatLine(k: string, v: unknown): string {
  return `  ${k.padEnd(30)}│ ${formatVal(v)}`;
}

function devLogEvent(sName: string, eName: string, userFacets: Record<string, unknown>, autoFacets: Record<string, unknown>) {
  devEventSeq += 1;
  const userLines = Object.entries(userFacets).map(([k, v]) => formatLine(k, v));
  const autoLines = Object.entries(autoFacets).map(([k, v]) => formatLine(k, v));
  const sep = '  ──────────────────────────────┼──────────────────────────────';

  console.groupCollapsed(
    `%c[TRACK]%c #${devEventSeq}  ${sName}: ${eName}`,
    'background:#00804A;color:#fff;padding:1px 6px;border-radius:3px;font-weight:bold',
    'color:#1a1a1a;font-weight:bold;font-size:12px',
  );
  console.log([...userLines, sep, ...autoLines].join('\n'));
  console.groupEnd();
}

const crmTracker = {
  sendEvent: (stateName: StateNameType, eventName: EventType, facets?: object) => {
    // Retrieve common facts each time before send event to ensure updated value is used in case of any changes
    const state = store.getState();

    const userFacets = omitBy({ ...facets }, isNil);
    const autoFacets = omitBy(genCommonFacets(stateName, { rootState: state }), isNil);
    const merged = { ...userFacets, ...autoFacets };

    if (!isProdEnv()) {
      devLogEvent(stateName, eventName, userFacets as Record<string, unknown>, autoFacets as Record<string, unknown>);
    }

    tracker.sendEvent(eventName, merged);
  },
};

const genCommonFacets = (module: StateNameType, state: TrackingState) => {
  return {
    state: module,
    ..._genCommonFacets(state),
  };
};

const _deviceType = jsBridge.canUseBridge() ? DEVICE_MOBILE : DEVICE_DESKTOP;
const _platform = jsBridge.getPlatform() || 'web';

const _genCommonFacets = (state: TrackingState) => {
  const profileInfo = getProfileInfoForInstrumentation();

  return {
    deviceType: _deviceType,
    PLATFORM: _platform,
    ...profileInfo,
  };
};

export default crmTracker;
